import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    FireFilled,
    LinkOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import {
    Table as AntTable,
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    Flex,
    Row,
    Tag,
    Typography,
} from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';

import {
    PayPeriodShiftDto,
    PayrollEmployeeCheckDto,
    ShiftType,
} from '@clh/api-client';
import {
    calculateTotalPay,
    calculateTotalStipend,
    HumlaDateTime,
    roundToAtMostTwoDecimals,
    toDollarAmount,
    toHourAndMinuteDisplay,
} from '@clh/util';

import { ActionButton } from '../action-button';
import { useApiClient } from '../hooks/use-api-client';
import shifts from '../shifts/shifts';
import Spinner from '../spinner';

import usePayPeriodContext from './pay-period-context';
import { getZealCheckUrl } from './zeal-url';

const { Text } = Typography;

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(amount / 100);

const OVERTIME_THRESHOLD = 40;

function ZealApiDataComponent({
    check,
    totalGrossPayPlatform,
}: {
    check: PayrollEmployeeCheckDto;
    totalGrossPayPlatform: number;
}) {
    return (
        <Card size="small" style={{ height: '100%' }}>
            <Flex
                justify="space-between"
                align="center"
                style={{ marginBottom: 16 }}
            >
                <Typography.Text strong style={{ color: '#fa8c16' }}>
                    ZEAL API DATA
                </Typography.Text>
            </Flex>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Text type="secondary">Zeal Employee Check Id:</Text>
                    <div>
                        <Text code={true} copyable={true}>
                            {check.zealEmployeeCheckId}
                        </Text>
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Check Date:</Text>
                    <div>{check.checkDate?.toLocaleDateString() || '-'}</div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Gross Pay:</Text>
                    <Flex align="center" gap={8}>
                        <div style={{ fontWeight: 500 }}>
                            {formatCurrency(check.grossPay || 0)}
                        </div>
                        {(check.grossPay || 0) === totalGrossPayPlatform ? (
                            <CheckCircleOutlined
                                style={{ color: '#52c41a', fontSize: '16px' }}
                                title="Matches platform amount"
                            />
                        ) : (
                            <>
                                <CloseCircleOutlined
                                    style={{
                                        color: '#ff4d4f',
                                        fontSize: '16px',
                                    }}
                                    title="Does not match platform amount"
                                />
                                <Text
                                    type="danger"
                                    style={{ fontSize: '12px' }}
                                >
                                    Mismatch
                                </Text>
                            </>
                        )}
                    </Flex>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Overtime Pay:</Text>
                    <div style={{ fontWeight: 500 }}>
                        {formatCurrency(check.overtimePay || 0)}
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Status:</Text>
                    <div>
                        <Tag color={check.isApproved ? 'green' : 'orange'}>
                            {check.isApproved ? 'Approved' : 'Pending'}
                        </Tag>
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Processed At:</Text>
                    <div>
                        {check.processedAt
                            ? check.processedAt.toLocaleString()
                            : '-'}
                    </div>
                </Col>
                <Col span={8}>
                    <Text type="secondary">Approval Status:</Text>
                    <div style={{ marginTop: 4 }}>
                        {check.isApproved ? (
                            <Badge status="success" text="Approved" />
                        ) : (
                            <Badge status="warning" text="Unapproved" />
                        )}
                    </div>
                </Col>
            </Row>
        </Card>
    );
}

function ShiftDetails({
    check,
    totalGrossPay,
}: {
    check: PayrollEmployeeCheckDto;
    totalGrossPay: number;
}) {
    const shifts = check.payPeriods.map((payPeriod) => ({
        ...payPeriod.shift,
        payPeriodMinutesWorked: payPeriod.minutesWorked || 0,
        payPeriodTotalMinutesWorked:
            (payPeriod.minutesWorked || 0) +
            (payPeriod.onCallMinutesWorked || 0),
        isFirstPayPeriod: payPeriod.isFirstPayPeriod,
        id: payPeriod.shiftId,
    }));

    return (
        <Flex justify="space-between">
            <AntTable<
                PayPeriodShiftDto & {
                    id: string;
                    payPeriodMinutesWorked: number;
                    payPeriodTotalMinutesWorked: number;
                    isFirstPayPeriod: boolean;
                }
            >
                size="small"
                showHeader={true}
                pagination={false}
                footer={() => (
                    <Flex justify="end">
                        <div style={{ marginRight: 10 }}>
                            Total Gross Pay: <br />
                            (platform)
                        </div>
                        <Typography.Title
                            level={5}
                            style={{ padding: 0, margin: 0 }}
                        >
                            {toDollarAmount(totalGrossPay)}
                        </Typography.Title>
                    </Flex>
                )}
                columns={[
                    {
                        dataIndex: 'facility',
                        title: 'Facility',
                        render: (facility, shift) => (
                            <Button
                                type="link"
                                target="_blank"
                                href={`/shifts/${shift.id}`}
                                icon={<LinkOutlined />}
                                style={{ padding: 0, height: 'auto' }}
                            >
                                {facility.name}
                            </Button>
                        ),
                        ellipsis: true,
                    },
                    {
                        dataIndex: 'specialtyCode',
                        render: (_specialty, shift) => (
                            <>
                                {shift.specialtyCode && (
                                    <Tag>{shift.specialtyCode}</Tag>
                                )}
                                <Tag>{shift.shiftType.toLowerCase()}</Tag>
                            </>
                        ),
                    },
                    {
                        dataIndex: 'startTime',
                        width: 280,
                        title: 'Scheduled time',
                        render: (_startTime, shift) => (
                            <Typography.Text type="secondary">
                                {DateTime.fromJSDate(
                                    shift.startTime
                                ).toLocaleString(
                                    HumlaDateTime.DATETIME_SHORT
                                )}{' '}
                                -{' '}
                                {DateTime.fromJSDate(
                                    shift.endTime
                                ).toLocaleString(HumlaDateTime.DATETIME_SHORT)}
                            </Typography.Text>
                        ),
                    },
                    {
                        title: 'Hours Worked',
                        dataIndex: 'payPeriodTotalMinutesWorked',
                        render: (payPeriodTotalMinutesWorked, shift) => (
                            <>
                                {toHourAndMinuteDisplay(
                                    payPeriodTotalMinutesWorked,
                                    true
                                )}

                                {payPeriodTotalMinutesWorked <
                                (shift.minutesWorked || 0) +
                                    (shift.onCallShift?.minutesWorked || 0)
                                    ? ' (Partial)'
                                    : null}
                            </>
                        ),
                    },
                    {
                        render: (_, shift) => {
                            const cellStyle = {
                                textAlign: 'center' as const,
                                lineHeight: '12px',
                                paddingLeft: 8,
                                paddingRight: 8,
                            };

                            // Check if this is an On-Call shift
                            const isOnCall =
                                shift.shiftType === ShiftType.OnCall &&
                                shift.onCallShift;

                            return (
                                <Flex justify="end">
                                    <table style={{ width: 'auto' }}>
                                        <tbody>
                                            <tr>
                                                {isOnCall ? (
                                                    // On-Call shift formula
                                                    <>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                (shift.onCallShift!
                                                                    .minutesWorked ||
                                                                    0) / 60
                                                            )}
                                                            <br />
                                                            <small>
                                                                hrs on-call
                                                            </small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.onCallShift!
                                                                    .payRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                on-call rate
                                                            </small>
                                                        </td>
                                                        <td>+</td>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                shift.payPeriodMinutesWorked /
                                                                    60
                                                            )}
                                                            <br />
                                                            <small>
                                                                hrs Called back
                                                            </small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.payRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                Called back rate
                                                            </small>
                                                        </td>
                                                        {shift.stipends &&
                                                        shift.isFirstPayPeriod &&
                                                        shift.stipends.length >
                                                            0 ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        calculateTotalStipend(
                                                                            shift.stipends?.filter(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.qualified
                                                                            )
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        stipend
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        <td>=</td>
                                                    </>
                                                ) : (
                                                    // Standard shift formula (existing logic)
                                                    <>
                                                        <td style={cellStyle}>
                                                            {roundToAtMostTwoDecimals(
                                                                shift.payPeriodMinutesWorked /
                                                                    60
                                                            )}
                                                            <br />
                                                            <small>hrs</small>
                                                        </td>
                                                        <td>×</td>
                                                        <td style={cellStyle}>
                                                            {toDollarAmount(
                                                                shift.payRate
                                                            )}
                                                            <br />
                                                            <small>
                                                                pay rate
                                                            </small>
                                                        </td>
                                                        {shift.stipends &&
                                                        shift.isFirstPayPeriod &&
                                                        shift.stipends.length >
                                                            0 ? (
                                                            <>
                                                                <td>+</td>
                                                                <td
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {toDollarAmount(
                                                                        calculateTotalStipend(
                                                                            shift.stipends?.filter(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.qualified
                                                                            )
                                                                        )
                                                                    )}
                                                                    <br />
                                                                    <small>
                                                                        stipend
                                                                    </small>
                                                                </td>
                                                            </>
                                                        ) : null}
                                                        <td>=</td>
                                                    </>
                                                )}
                                            </tr>
                                        </tbody>
                                    </table>
                                </Flex>
                            );
                        },
                    },
                    {
                        dataIndex: 'totalRate',
                        title: 'Gross Pay',
                        width: 100,
                        render: (_totalRate, shift) => {
                            let grossPay: number;

                            if (
                                shift.shiftType === ShiftType.OnCall &&
                                shift.onCallShift
                            ) {
                                // On-Call shift calculation
                                const onCallPay =
                                    (shift.onCallShift.payRate *
                                        (shift.onCallShift.minutesWorked ||
                                            0)) /
                                    60;
                                const calledInPay =
                                    (shift.payRate *
                                        shift.payPeriodMinutesWorked) /
                                    60;
                                const stipend = calculateTotalStipend(
                                    shift.isFirstPayPeriod
                                        ? shift.stipends?.filter(
                                              (s) => s.qualified
                                          )
                                        : []
                                );
                                grossPay = onCallPay + calledInPay + stipend;
                            } else {
                                // Standard shift calculation
                                grossPay = calculateTotalPay(
                                    shift.payRate,
                                    shift.payPeriodMinutesWorked,
                                    calculateTotalStipend(
                                        shift.isFirstPayPeriod
                                            ? shift.stipends?.filter(
                                                  (s) => s.qualified
                                              )
                                            : []
                                    )
                                );
                            }

                            return <b>{formatCurrency(grossPay)}</b>;
                        },
                    },
                ]}
                dataSource={shifts}
            />
        </Flex>
    );
}

export function EmployeeCheck({
    check,
    fetchEmployeeChecks,
}: {
    check: PayrollEmployeeCheckDto;
    fetchEmployeeChecks: () => Promise<void>;
}) {
    const api = useApiClient();
    const [activeKeys, setActiveKeys] = useState<string[]>(
        check.isApproved ? [] : ['paycheck']
    );

    const zealUrl = useMemo(
        () => getZealCheckUrl(check),
        [check.status, check.zealEmployeeId, check.zealEmployeeCheckId]
    );

    const totalGrossPay = useMemo(
        () =>
            check?.payPeriods.reduce(
                (total, payPeriod) =>
                    total +
                    calculateTotalPay(
                        payPeriod.shift.payRate,
                        payPeriod.minutesWorked!,
                        calculateTotalStipend(
                            payPeriod.isFirstPayPeriod
                                ? payPeriod.shift.stipends?.filter(
                                      (s) => s.qualified
                                  )
                                : []
                        ) +
                            (payPeriod.onCallMinutesWorked &&
                            payPeriod.shift.onCallShift
                                ? calculateTotalPay(
                                      payPeriod.shift.onCallShift.payRate,
                                      payPeriod.onCallMinutesWorked,
                                      0
                                  )
                                : 0)
                    ),
                0
            ),
        [shifts]
    );

    const totalMinutesWorked = useMemo(
        () =>
            check?.payPeriods.reduce(
                (total, payPeriod) =>
                    total + (payPeriod.minutesWorked || 0) / 60,
                0
            ),
        [check.payPeriods]
    );

    const totalOnCallHours = useMemo(
        () =>
            check?.payPeriods.reduce(
                (total, payPeriod) =>
                    total + (payPeriod.onCallMinutesWorked || 0) / 60,
                0
            ),
        [check.payPeriods]
    );

    const totalHoursWorked = totalMinutesWorked / 60;

    return (
        <Collapse
            key={check.id}
            size="small"
            style={{
                marginBottom: 16,
            }}
            activeKey={activeKeys}
            onChange={setActiveKeys}
            items={[
                {
                    key: 'paycheck',
                    label: (
                        <Flex
                            justify="space-between"
                            align="center"
                            style={{ width: '100%' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Flex align="center" gap={8}>
                                {totalHoursWorked > OVERTIME_THRESHOLD && (
                                    <FireFilled
                                        style={{
                                            color: '#ff4d4f',
                                            fontSize: '16px',
                                        }}
                                    />
                                )}
                                <Typography.Title
                                    level={5}
                                    style={{ margin: 0 }}
                                >
                                    {check.employeeName}
                                </Typography.Title>
                                <Button
                                    size="small"
                                    type="link"
                                    icon={<LinkOutlined />}
                                    target="_blank"
                                    href={`/profiles/${check.profileId}`}
                                    style={{ padding: 0, height: 'auto' }}
                                >
                                    View Profile
                                </Button>
                            </Flex>
                            <Flex align="center" gap={12}>
                                <div style={{ marginRight: 8 }}>
                                    <Text
                                        type="secondary"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Total Hours Worked:{' '}
                                    </Text>
                                    <Text style={{ fontWeight: 500 }}>
                                        {toHourAndMinuteDisplay(
                                            totalMinutesWorked,
                                            true
                                        )}
                                    </Text>
                                    {totalHoursWorked > OVERTIME_THRESHOLD && (
                                        <Tag
                                            color="red"
                                            style={{ marginLeft: 4 }}
                                        >
                                            Overtime
                                        </Tag>
                                    )}
                                </div>
                                {totalOnCallHours > 0 && (
                                    <div style={{ marginRight: 8 }}>
                                        <Text
                                            type="secondary"
                                            style={{ fontSize: '12px' }}
                                        >
                                            On-Call Hours:{' '}
                                        </Text>
                                        <Text style={{ fontWeight: 500 }}>
                                            {roundToAtMostTwoDecimals(
                                                totalOnCallHours
                                            )}
                                        </Text>
                                    </div>
                                )}
                                <Button
                                    href={zealUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    size="small"
                                    icon={<LinkOutlined />}
                                >
                                    Open in Zeal
                                </Button>
                                {check.isApproved ? (
                                    <ActionButton
                                        size="small"
                                        danger
                                        action={async () => {
                                            await api?.payrollControllerUnapproveEmployeeCheck(
                                                {
                                                    employeeCheckId:
                                                        check.zealEmployeeCheckId,
                                                }
                                            );
                                            await fetchEmployeeChecks();
                                            setActiveKeys(['paycheck']);
                                        }}
                                        successMessage="Employee check unapproved successfully"
                                        confirm="Are you sure you want to unapprove this employee check?"
                                    >
                                        Unapprove
                                    </ActionButton>
                                ) : (
                                    <ActionButton
                                        size="small"
                                        type="primary"
                                        action={async () => {
                                            await api?.payrollControllerApproveEmployeeCheck(
                                                {
                                                    employeeCheckId:
                                                        check.zealEmployeeCheckId,
                                                }
                                            );
                                            await fetchEmployeeChecks();
                                            setActiveKeys([]);
                                        }}
                                        successMessage="Employee check approved successfully"
                                    >
                                        Approve
                                    </ActionButton>
                                )}
                            </Flex>
                        </Flex>
                    ),
                    children: (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            <Card
                                size="small"
                                style={{
                                    backgroundColor: '#f8f9ff',
                                }}
                            >
                                <Typography.Text
                                    strong
                                    style={{
                                        color: '#000',
                                        marginBottom: 8,
                                        display: 'block',
                                    }}
                                >
                                    PLATFORM DATA - {check.payPeriods.length}{' '}
                                    Shift
                                    {check.payPeriods.length !== 1 && 's'}
                                </Typography.Text>
                                <ShiftDetails
                                    check={check}
                                    totalGrossPay={totalGrossPay}
                                />
                            </Card>
                            <ZealApiDataComponent
                                check={check}
                                totalGrossPayPlatform={totalGrossPay}
                            />
                        </div>
                    ),
                },
            ]}
        />
    );
}

function PayrollSummary({
    employeeChecks,
    onForceSync,
    isSyncing,
}: {
    employeeChecks: PayrollEmployeeCheckDto[];
    onForceSync: () => Promise<void>;
    isSyncing: boolean;
}) {
    const totalEmployeeChecks = employeeChecks.length;
    const totalShifts = employeeChecks.reduce(
        (total, check) => total + check.payPeriods.length,
        0
    );
    const totalGrossPay = employeeChecks.reduce(
        (total, check) =>
            total +
            check.payPeriods.reduce(
                (checkTotal, payPeriod) =>
                    checkTotal +
                    calculateTotalPay(
                        payPeriod.shift.payRate,
                        payPeriod.minutesWorked!,
                        calculateTotalStipend(
                            payPeriod.shift.stipends?.filter((s) => s.qualified)
                        )
                    ) +
                    (payPeriod.onCallMinutesWorked &&
                    payPeriod.shift.onCallShift
                        ? calculateTotalPay(
                              payPeriod.shift.onCallShift.payRate,
                              payPeriod.onCallMinutesWorked,
                              0
                          )
                        : 0),
                0
            ),
        0
    );

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
                <Flex align="center" gap={8}>
                    <Typography.Title
                        level={5}
                        style={{ margin: 0, padding: 0 }}
                    >
                        Payroll Summary
                    </Typography.Title>
                    <Button
                        type="text"
                        icon={<SyncOutlined spin={isSyncing} />}
                        onClick={() => void onForceSync()}
                        loading={isSyncing}
                        title="Sync data from Zeal"
                        size="small"
                    />
                </Flex>
                <Flex gap={24}>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#fa8c16',
                            }}
                        >
                            {formatCurrency(totalGrossPay)}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Gross Pay
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#1890ff',
                            }}
                        >
                            {totalEmployeeChecks}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Employee Checks
                        </Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#52c41a',
                            }}
                        >
                            {totalShifts}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Total Shifts
                        </Text>
                    </div>
                </Flex>
            </Flex>
        </Card>
    );
}

export default function Paychecks() {
    const api = useApiClient();
    const [employeeChecks, setEmployeeChecks] = useState<
        PayrollEmployeeCheckDto[]
    >([]);
    const [loading, setLoading] = useState(false);

    const { startDate: payPeriodStart, endDate: payPeriodEnd } =
        usePayPeriodContext();

    const fetchEmployeeChecks = async (forceSync = false) => {
        if (!api) return;

        setLoading(true);
        try {
            const checks = await api.payrollControllerGetPaychecks({
                payPeriodStart,
                payPeriodEnd,
                forceSync: forceSync ? true : undefined,
            });

            setEmployeeChecks(checks);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchEmployeeChecks();
    }, [payPeriodStart.toISOString(), payPeriodEnd.toISOString()]);

    if (!api) {
        return <Spinner />;
    }

    return (
        <>
            <PayrollSummary
                employeeChecks={employeeChecks}
                onForceSync={() => fetchEmployeeChecks(true)}
                isSyncing={loading}
            />
            {employeeChecks.map((check) => (
                <EmployeeCheck
                    key={check.id}
                    check={check}
                    fetchEmployeeChecks={fetchEmployeeChecks}
                />
            ))}
        </>
    );
}
